import { CreateTextDialog } from '@/components/editor/CreateTextDialog';
import { FloatingEntityCard } from '@/components/editor/FloatingEntityCard';
import { RichTextEditor } from '@/components/editor/rich-text-editor';
import { EnrichedEntity } from '@/lib/types';
import { MenuBook as BookOpen, FullscreenExit } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RefObject, useState } from 'react';

import { useTranslation } from 'react-i18next';

interface EditorMainAreaProps {
  editorAreaRef: RefObject<HTMLDivElement>;
  isLoadingText: boolean;
  selectedTextId?: string;
  editorContent: string;
  setEditorContent: (content: string) => void;
  getPlaceholder: () => string;
  handleDrop: (event: React.DragEvent) => void;
  handleDragOver: (event: React.DragEvent) => void;
  floatingCards: Map<string, { entity: EnrichedEntity; position: { x: number; y: number } }>;
  handleCloseCard: (entityId: string) => void;
  handleCardPositionChange: (entityId: string, position: { x: number; y: number }) => void;
  handleFloatingCardClick: (entityId: string, event: React.MouseEvent) => void;
  onCreateText: (title: string) => Promise<void>;
  titleInput?: React.ReactNode;
  wordCount?: number;
  characterCount?: number;
  isAutosaving?: boolean;
  autosaveEnabled?: boolean;
  isFocusMode?: boolean;
  onToggleFocusMode?: () => void;
  onSave?: (content?: string, silent?: boolean, skipHistory?: boolean) => Promise<void>;
  onCommentChange?: (content: string) => void | Promise<void>;
  entityHighlights?: boolean;
  entities?: EnrichedEntity[];
}

export function EditorMainArea({
  editorAreaRef,
  isLoadingText,
  selectedTextId,
  editorContent,
  setEditorContent,
  getPlaceholder,
  handleDrop,
  handleDragOver,
  floatingCards,
  handleCloseCard,
  handleCardPositionChange,
  handleFloatingCardClick,
  onCreateText,
  titleInput,
  wordCount = 0,
  characterCount = 0,
  isAutosaving = false,
  autosaveEnabled = false,
  isFocusMode = false,
  onToggleFocusMode,
  onSave,
  onCommentChange,
  entityHighlights = false,
  entities = [],
}: EditorMainAreaProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <Box
      ref={editorAreaRef}
      id="editor-main-area-step"
      sx={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}
      onDropCapture={handleDrop}
      onDragOverCapture={handleDragOver}
    >
      {isFocusMode && (
        <Tooltip title={t('editor.exit_focus')}>
          <IconButton
            onClick={onToggleFocusMode}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <FullscreenExit />
          </IconButton>
        </Tooltip>
      )}
      {titleInput}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {isLoadingText ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {t('editor.loading_text')}
              </Typography>
            </Box>
          ) : selectedTextId ? (
            <RichTextEditor
              key={selectedTextId}
              content={editorContent}
              onChange={setEditorContent}
              onCommentChange={onCommentChange}
              onSave={onSave}
              placeholder={getPlaceholder()}
              textId={selectedTextId}
              entityHighlights={entityHighlights}
              entities={entities}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <BookOpen sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {t('editor.ready_to_write')}
              </Typography>
              <Typography variant="body1">
                {t('editor.ready_to_write_desc_1')}
                <br />
                {t('editor.ready_to_write_desc_2')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsDialogOpen(true)}
                sx={{ mt: 3, px: 4, borderRadius: 2 }}
              >
                {t('texts.start_writing')}
              </Button>
            </Box>
          )}

          {/* Floating Entity Cards */}
          {Array.from(floatingCards.entries()).map(([entityId, { entity, position }]) => (
            <FloatingEntityCard
              key={entityId}
              entity={entity}
              initialPosition={position}
              onClose={() => handleCloseCard(entityId)}
              onPositionChange={(pos) => handleCardPositionChange(entityId, pos)}
              onEntityClick={(event) => handleFloatingCardClick(entityId, event)}
            />
          ))}
        </Box>

        {/* Footer Status Bar */}
        <Box
          sx={{
            p: 1.5,
            px: 3,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {wordCount} {t('editor.words')} | {characterCount} {t('editor.characters_count')} | {Math.ceil(wordCount / 200)} min {t('editor.reading_time')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAutosaving ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={12} color="inherit" sx={{ opacity: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {t('editor.saving')}
                </Typography>
              </Stack>
            ) : autosaveEnabled && selectedTextId ? (
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                {t('editor.auto_saved')}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>

      <CreateTextDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCreate={onCreateText}
      />
    </Box>
  );
}
